import _ from 'lodash';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { FULL_EVENT_TYPE_OPTIONS } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import type { DeviceFilterModalProps } from './deviceFilterModal';

/**
 * Backs the advanced filter modal for device events with its date, time, event type and validity selections. Holds the pending filter in merged state seeded from the props on mount, exposes per-field change handlers that keep the start and stop range consistent, and on apply either raises per-field errors or hands the selection to the parent's handleClickFilterButton.
 *
 * @param props - Modal props carrying the applied and default date range, event type, validity and the apply callback.
 * @returns An object carrying the pending filter state, the event type option list and the field change, reset and apply handlers.
 */
const useDeviceFilterModal = ({
  handleClickFilterButton,
  defaultEndDate,
  defaultStartDate,
  startDate,
  endDate,
  isValid,
  eventType,
}: Pick<
  DeviceFilterModalProps,
  | 'handleClickFilterButton'
  | 'defaultEndDate'
  | 'defaultStartDate'
  | 'startDate'
  | 'endDate'
  | 'isValid'
  | 'eventType'
>) => {
  const eventTypeList = useRef(['All type', ...FULL_EVENT_TYPE_OPTIONS, 'Manual']);

  const defaultFilterObject = useRef({
    startSearchDate: undefined,
    startSearchDateString: '',
    startSearchTime: undefined,
    startSearchTimeString: '',
    stopSearchDate: undefined,
    stopSearchDateString: '',
    stopSearchTime: undefined,
    stopSearchTimeString: '',

    eventType: 'All type',
    validity: 'All validity',
  });

  const [state, setState] = useMergeState<any>({
    ...defaultFilterObject.current,
    errorObject: {},
  });

  /**
   * Seeds the device event filter modal from either the currently applied filter or the study's full recording range. Picks the default start and end dates when resetting and the applied filter dates otherwise, formats each into its date and time string, sets event type and validity to their All options or the applied values, and clears the error object.
   *
   * @param isResetFilter - Whether to fall back to the study's default range and unfiltered options instead of the applied filter.
   * @returns Nothing.
   */
  const createDefaultData = (isResetFilter = false) => {
    const startSearchDateString = isResetFilter
      ? defaultStartDate?.format('MM/DD/YYYY')
      : startDate?.format('MM/DD/YYYY');
    const startSearchTimeString = isResetFilter
      ? defaultStartDate?.format('HH:mm')
      : startDate?.format('HH:mm');
    const stopSearchDateString = isResetFilter
      ? defaultEndDate?.format('MM/DD/YYYY')
      : endDate?.format('MM/DD/YYYY');
    const stopSearchTimeString = isResetFilter
      ? defaultEndDate?.format('HH:mm')
      : endDate?.format('HH:mm');

    setState({
      startSearchDate: isResetFilter ? defaultStartDate : startDate,
      startSearchDateString,
      startSearchTime: isResetFilter ? defaultStartDate : startDate,
      startSearchTimeString,
      stopSearchDate: isResetFilter ? defaultEndDate : endDate,
      stopSearchDateString,
      stopSearchTime: isResetFilter ? defaultEndDate : endDate,
      stopSearchTimeString,
      eventType: isResetFilter ? 'All type' : eventType || 'All type',
      validity: isResetFilter
        ? 'All validity'
        : _.isNil(isValid)
          ? 'All validity'
          : isValid
            ? 'Valid'
            : 'Invalid',

      errorObject: {},
    });
  };

  /**
   * Applies a new start date to the device event filter while keeping the selected range valid. Stores the date and its string, and when the current stop date now precedes it, shifts the stop date forward by the previous span, clamped to the study's default end date, or onto the same day when there was no span.
   *
   * @param date - Moment value picked for the range start.
   * @param dateString - Formatted MM/DD/YYYY text for the picked date.
   * @returns Nothing.
   */
  const onChangeStartSearchDate = (date: any, dateString: any) => {
    const setStateObject = { startSearchDate: date, startSearchDateString: dateString };
    if (state.stopSearchDate?.isBefore(date)) {
      // *: Keep previous date range
      const searchDateRange = state.stopSearchDate.diff(state.startSearchDate, 'days');
      let stopSearchDate = moment(defaultEndDate);
      if (searchDateRange) {
        stopSearchDate = moment(date).add(searchDateRange, 'days');
        if (stopSearchDate.isAfter(defaultEndDate)) {
          stopSearchDate = moment(defaultEndDate);
        }
      } else {
        stopSearchDate = moment(date);
      }
      _.assign(setStateObject, {
        stopSearchDate,
        stopSearchDateString: stopSearchDate.format('MM/DD/YYYY'),
      });
    }
    setState(setStateObject);
  };

  /**
   * Records the start-of-range time picked in the device event filter. Merges the moment value and its formatted HH:mm string into the modal state.
   *
   * @param time - Moment value produced by the time picker.
   * @param timeString - Formatted HH:mm text for the picked time.
   * @returns Nothing.
   */
  const onChangeStartSearchTime = (time: any, timeString: any) => {
    setState({ startSearchTime: time, startSearchTimeString: timeString });
  };

  /**
   * Records the end-of-range date picked in the device event filter. Merges the moment value and its formatted MM/DD/YYYY string into the modal state.
   *
   * @param date - Moment value produced by the date picker.
   * @param dateString - Formatted MM/DD/YYYY text for the picked date.
   * @returns Nothing.
   */
  const onChangeStopSearchDate = (date: any, dateString: any) => {
    setState({ stopSearchDate: date, stopSearchDateString: dateString });
  };

  /**
   * Records the end-of-range time picked in the device event filter. Merges the moment value and its formatted HH:mm string into the modal state.
   *
   * @param time - Moment value produced by the time picker.
   * @param timeString - Formatted HH:mm text for the picked time.
   * @returns Nothing.
   */
  const onChangestopSearchTime = (time: any, timeString: any) => {
    setState({ stopSearchTime: time, stopSearchTimeString: timeString });
  };

  /**
   * Stores a single advanced-filter field edit in the device event filter modal. Merges the named key and its value into the modal's local state.
   *
   * @param name - Filter field being changed, such as eventType or validity.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Restores the device event filter modal to the study's full range and unfiltered defaults. Calls createDefaultData with the reset flag, which reseeds the dates, times, event type and validity.
   *
   * @returns Nothing.
   */
  const onClickResetFilterButton = () => {
    createDefaultData(true);
  };

  /**
   * Validates the pending device event filter and applies it when clean. Requires a date wherever a time is set and rejects a start that falls after the stop, writing per-field Invalid value entries into a cloned error object; when nothing is left in it the errors are cleared and a copy of the filter state goes to the parent's handleClickFilterButton.
   *
   * @returns Nothing.
   */
  const onClickFilterButton = () => {
    const clonedErrorObject = _.cloneDeep(state.errorObject);

    if (state.startSearchTime) {
      if (!state.startSearchDate) {
        _.assign(clonedErrorObject, { startSearchDate: 'Invalid value' });
      } else {
        delete clonedErrorObject.startSearchDate;
      }
    } else {
      delete clonedErrorObject.startSearchDate;
    }

    if (state.stopSearchTime) {
      if (!state.stopSearchDate) {
        _.assign(clonedErrorObject, { stopSearchDate: 'Invalid value' });
      } else {
        delete clonedErrorObject.stopSearchDate;
      }
    } else {
      delete clonedErrorObject.stopSearchDate;
    }

    if (
      state.startSearchDate &&
      state.startSearchTime &&
      state.stopSearchDate &&
      state.stopSearchTime
    ) {
      const startSearchDate = moment(
        `${state.startSearchDateString} ${state.startSearchTimeString}`,
      );
      const stopSearchDate = moment(`${state.stopSearchDateString} ${state.stopSearchTimeString}`);
      if (startSearchDate.isAfter(stopSearchDate)) {
        _.assign(clonedErrorObject, {
          startSearchTime: 'Invalid value',
          stopSearchTime: 'Invalid value',
        });
      } else {
        delete clonedErrorObject.startSearchTime;
        delete clonedErrorObject.stopSearchTime;
      }
    } else {
      delete clonedErrorObject.startSearchTime;
      delete clonedErrorObject.stopSearchTime;
    }

    if (_.isEmpty(clonedErrorObject)) {
      setState({ errorObject: clonedErrorObject });

      handleClickFilterButton({ ...state });
    } else {
      setState({ errorObject: clonedErrorObject });
    }
  };
  useEffect(() => {
    createDefaultData();
  }, []);

  return {
    state,
    eventTypeList,
    onChangeStartSearchDate,
    onChangeStartSearchTime,
    onChangeStopSearchDate,
    onChangestopSearchTime,
    onChange,
    onClickResetFilterButton,
    onClickFilterButton,
  };
};

export default useDeviceFilterModal;
